import React from 'react';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { partners } from '../data/mock';
import { Users, Building, Truck, Landmark, Handshake, Globe, Shield, TrendingUp, Package, FileCheck, Target } from 'lucide-react';
import { SEO } from '../components/SEO';

const categoryIcons = {
  Buyers: Users,
  Suppliers: Building,
  'Logistics Partners': Truck,
  'Trade Bodies': Landmark,
};

export default function PartnersPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Partners"
        path="/partners"
        description="Partner with Naviton Global for reliable international trade solutions. Join our growing network of buyers, suppliers, and logistics partners."
      />
      <Header />
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-20 bg-navy-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="inline-block px-4 py-1.5 rounded-full bg-teal-500/20 text-teal-400 text-sm font-medium mb-6">
                Our Network
              </span>
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
                Partners & <span className="text-teal-400">Network</span>
              </h1>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto">
                A trusted network of buyers, suppliers, logistics partners, and trade bodies 
                that enables seamless international trade in both directions.
              </p>
            </div>
          </div>
        </section>

        {/* Partner Categories */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {partners.types.map((partner) => {
                const Icon = categoryIcons[partner.category];
                return (
                  <div
                    key={partner.category}
                    className="p-8 rounded-2xl bg-slate-50 border border-slate-200 hover:border-teal-300 hover:shadow-lg transition-all duration-300 text-center"
                  >
                    <div className="p-4 w-fit mx-auto rounded-xl bg-navy-900 mb-6">
                      <Icon className="h-8 w-8 text-white" />
                    </div>
                    <div className="text-4xl font-bold text-teal-600 mb-2">{partner.count}</div>
                    <h3 className="text-xl font-semibold text-navy-900 mb-2">{partner.category}</h3>
                    <p className="text-slate-600 text-sm">{partner.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Partnership Benefits */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-navy-900 mb-4">Why Partner With Us</h2>
              <p className="text-xl text-slate-600">Benefits of joining our global trade network</p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { title: 'Market Access', desc: 'Connect with verified buyers and suppliers across 50+ countries', icon: Globe },
                { title: 'Trade Support', desc: 'Full documentation, compliance, and logistics coordination', icon: FileCheck },
                { title: 'Quality Assurance', desc: 'Rigorous vetting and quality control for all partners', icon: Shield },
                { title: 'Competitive Pricing', desc: 'Leverage our network for better trade terms', icon: TrendingUp },
                { title: 'Risk Mitigation', desc: 'Due diligence and secure trade practices', icon: Target },
                { title: 'Growth Opportunities', desc: 'Expand into new markets with expert guidance', icon: Package },
              ].map((benefit) => {
                const Icon = benefit.icon;
                return (
                  <div key={benefit.title} className="bg-white p-6 rounded-2xl border border-slate-200">
                    <div className="p-3 w-fit rounded-xl bg-teal-100 mb-4">
                      <Icon className="h-6 w-6 text-teal-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-navy-900 mb-2">{benefit.title}</h3>
                    <p className="text-slate-600 text-sm">{benefit.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Partner Logos */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-navy-900 mb-4">Trusted By Industry Leaders</h2>
              <p className="text-slate-600">Our partners include leading organizations across sectors</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
              {[
                { name: 'Austrade', subtitle: 'Australian Trade Commission' },
                { name: 'ACCI', subtitle: 'Australian Chamber' },
                { name: 'Export Council', subtitle: 'of Australia' },
                { name: 'DAFF', subtitle: 'Agriculture & Fisheries' },
                { name: 'AusIndustry', subtitle: 'Business Support' },
                { name: 'EFIC', subtitle: 'Export Finance' },
              ].map((partner, i) => (
                <div key={i} className="h-24 bg-slate-50 rounded-xl flex flex-col items-center justify-center p-4 border border-slate-200 hover:border-teal-300 transition-colors">
                  <span className="text-navy-900 font-semibold text-sm text-center">{partner.name}</span>
                  <span className="text-slate-500 text-xs text-center">{partner.subtitle}</span>
                </div>
              ))}
            </div>
            <p className="text-center text-slate-500 text-sm mt-8">
              * Partner logos shown as placeholders. Actual partnerships may vary.
            </p>
          </div>
        </section>

        {/* Become a Partner CTA */}
        <section className="py-20 bg-navy-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Handshake className="h-16 w-16 text-teal-400 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white mb-4">
              Become a Partner
            </h2>
            <p className="text-lg text-slate-300 mb-8">
              Join our growing network of trade partners and unlock new opportunities 
              in international import and export markets.
            </p>
            <a href="/contact" className="inline-flex items-center px-8 py-4 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-xl transition-colors">
              Get in Touch
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
